/**
 * MegaMenuNav - Employee-specific mega-menu navigation
 *
 * Uses the shared MegaMenu component from tio-ui with employee-specific
 * navigation items and scenario-aware visibility filtering.
 */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { MegaMenu } from 'tio-ui/components/mega-menu';
import type { MegaMenuItem, MegaMenuDropdownItem } from 'tio-ui/components/mega-menu';
import type { DemoScenarioId } from 'tio-employee/utils/career-pathways-demo-data';
import {
  PhHouse,
  PhBank,
  PhGraduationCap,
  PhChartLine,
  PhHandshake,
  PhStar,
  PhCompass,
  PhCertificate,
  PhBooks,
  PhHeart,
  PhFolder,
  PhDotsThree,
  PhScales,
  PhPiggyBank,
} from 'ember-phosphor-icons';

interface MegaMenuNavSignature {
  Args: {
    /** Current demo scenario ID for feature visibility */
    scenarioId: DemoScenarioId;
    /**
     * Whether the employee has Student Loan Repayment. Gates the Employer Help
     * entry the way the production shell's `showSlr` benefit flag gates the
     * SLR nav row, since the base scenario id cannot tell SLR apart from PSLF.
     */
    showSlr: boolean;
    /** Whether the layout is in fullscreen mode (hides legacy frame) */
    isFullscreen?: boolean;
  };
  Element: HTMLElement;
}

/**
 * Extended dropdown item type that includes hideFor for scenario filtering and
 * requiresSlr for the benefit gate. This is internal to the wrapper — the
 * shared MegaMenu only sees isVisible.
 */
interface EmployeeDropdownItem {
  icon: typeof PhHouse;
  label: string;
  description: string;
  route: string;
  stat?: string;
  hideFor?: DemoScenarioId[];
  requiresSlr?: boolean;
}

interface EmployeeNavItemWithRoute {
  id: string;
  icon: typeof PhHouse;
  label: string;
  route: string;
  dropdown?: never;
  hideFor?: DemoScenarioId[];
}

interface EmployeeNavItemWithDropdown {
  id: string;
  icon: typeof PhHouse;
  label: string;
  route?: never;
  dropdown: EmployeeDropdownItem[];
  hideFor?: DemoScenarioId[];
}

type EmployeeNavItem = EmployeeNavItemWithRoute | EmployeeNavItemWithDropdown;

// Navigation configuration — same items as the old nav-bar, but scenario filtering
// is applied via isVisible before passing to the shared MegaMenu component.
const NAV_ITEMS: EmployeeNavItem[] = [
  {
    id: 'home',
    icon: PhHouse,
    label: 'Home',
    route: 'authenticated.reimagined.home',
  },
  {
    id: 'loans',
    icon: PhBank,
    label: 'Loans',
    hideFor: ['ta-only'],
    dropdown: [
      {
        icon: PhChartLine,
        label: 'Overview',
        description: 'See your full debt picture',
        route: 'authenticated.reimagined.loans',
      },
      {
        icon: PhHandshake,
        label: 'Employer Help',
        description: 'Track SLRP contributions',
        route: 'authenticated.reimagined.finance',
        stat: '$125/mo',
        requiresSlr: true,
      },
      {
        icon: PhStar,
        label: 'Forgiveness',
        description: 'PSLF tracker & tools',
        route: 'authenticated.reimagined.pslf',
        stat: '84/120',
      },
      {
        icon: PhCompass,
        label: 'Strategies',
        description: 'Find your best repayment plan',
        route: 'authenticated.reimagined.plan.strategy',
      },
    ],
  },
  {
    id: 'benefits',
    icon: PhGraduationCap,
    label: 'Benefits',
    hideFor: ['slrp-only'],
    dropdown: [
      {
        icon: PhGraduationCap,
        label: 'Overview',
        description: 'All your education benefits',
        route: 'authenticated.reimagined.benefits',
      },
      {
        icon: PhBooks,
        label: 'Tuition',
        description: 'Apply for courses',
        route: 'authenticated.reimagined.tas',
        stat: '$5,250/yr',
        hideFor: ['slrp-only', 'no-ta'],
      },
      {
        icon: PhCertificate,
        label: 'Certifications',
        description: 'Professional development',
        route: 'authenticated.reimagined.benefits',
        stat: '$2,500/yr',
      },
    ],
  },
  {
    id: 'more',
    icon: PhDotsThree,
    label: 'More',
    dropdown: [
      {
        icon: PhHeart,
        label: 'Wellness',
        description: 'Financial wellness score',
        route: 'authenticated.reimagined.wellness',
      },
      {
        icon: PhScales,
        label: 'IDR Plans',
        description: 'Income-driven repayment',
        route: 'authenticated.reimagined.idr',
        hideFor: ['ta-only'],
      },
      {
        icon: PhPiggyBank,
        label: 'Retirement',
        description: '401k & savings tools',
        route: 'authenticated.reimagined.syf',
      },
      {
        icon: PhGraduationCap,
        label: 'College Planning',
        description: '529 plans & scholarships',
        route: 'authenticated.reimagined.college-planning',
      },
      {
        icon: PhHandshake,
        label: 'Tuition Discounts',
        description: 'Education partner network',
        route: 'authenticated.reimagined.lpn',
      },
      {
        icon: PhFolder,
        label: 'Documents',
        description: 'Upload & manage files',
        route: 'authenticated.reimagined.documents',
      },
    ],
  },
];

export default class MegaMenuNav extends Component<MegaMenuNavSignature> {
  /**
   * Convert hideFor-based filtering to isVisible booleans.
   * Top-level items and dropdown items are both filtered.
   */
  @cached
  get navItems(): MegaMenuItem[] {
    const { scenarioId, showSlr } = this.args;

    return NAV_ITEMS.filter((item) => !item.hideFor || !item.hideFor.includes(scenarioId)).map(
      (item) => {
        if (item.dropdown) {
          const dropdown: MegaMenuDropdownItem[] = item.dropdown.map((d) => ({
            icon: d.icon,
            label: d.label,
            description: d.description,
            route: d.route,
            stat: d.stat,
            isVisible:
              (!d.hideFor || !d.hideFor.includes(scenarioId)) && (!d.requiresSlr || showSlr),
          }));

          return {
            id: item.id,
            icon: item.icon,
            label: item.label,
            dropdown,
          } as MegaMenuItem;
        }

        return {
          id: item.id,
          icon: item.icon,
          label: item.label,
          route: item.route,
        } as MegaMenuItem;
      }
    );
  }

  <template>
    <MegaMenu
      @items={{this.navItems}}
      @brandLabel="TIO"
      @isFullscreen={{@isFullscreen}}
      ...attributes
    />
  </template>
}
